import { useEffect, useMemo, type ReactNode } from 'react';
import { ImageUploader } from './ImageUploader';

/**
 * Image-picker variant for *create* flows, where the entity doesn't
 * exist yet so we can't upload to a real endpoint.
 *
 * Holds the user's pick as a `File | null` in parent state and shows
 * a same-origin `URL.createObjectURL(...)` preview inline. After the
 * parent creates the entity (and gets back an id), it can read the
 * staged `File` off its own state and call the matching upload hook.
 *
 * Why a wrapper around `ImageUploader`:
 *   - `ImageUploader`'s contract is `onUpload(file): Promise<void>`,
 *     designed for surfaces where there's a real network call. In the
 *     stage-then-upload flow we just want to remember the File, no
 *     async work needed. This shim adapts the two.
 *   - Object URLs need to be revoked on unmount / replacement to
 *     avoid leaks; co-locating that lifecycle here keeps every
 *     "create" screen from re-inventing it.
 */
export interface StagedImagePickerProps {
  /** Currently staged file (parent state). */
  file: File | null;
  /** Parent state setter. Pass `null` to clear. */
  onChange: (next: File | null) => void;
  /**
   * Render-prop for the preview. Receives the live preview URL
   * (object URL when a file is staged, else `null`) so the caller can
   * compose the right avatar (`BusinessAvatar` / `ClientAvatar`) with
   * the rest of its identity props.
   */
  renderPreview: (previewUrl: string | null) => ReactNode;
  title?: string;
  hint?: string;
}

export function StagedImagePicker({
  file,
  onChange,
  renderPreview,
  title,
  hint,
}: StagedImagePickerProps) {
  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );

  // Revoke the previous object URL once the File reference flips
  // (replace or remove). Without this, picking five logos before
  // submitting leaks five blob URLs into the document.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <ImageUploader
      preview={renderPreview(previewUrl)}
      onUpload={(picked) => onChange(picked)}
      onRemove={file ? () => onChange(null) : undefined}
      hasImage={!!file}
      title={title}
      hint={hint}
    />
  );
}
